import React, { useState } from "react";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleContext";
import api, { formatApiError, setAuthToken } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, Lock, UserCog, Check, ShieldAlert } from "lucide-react";

import SessionsCard from "@/components/SessionsCard";
import MfaCard from "@/components/MfaCard";
import ThemeCard from "@/components/ThemeCard";
import LocaleCard from "@/components/LocaleCard";
import CalendarConnectCard from "@/components/CalendarConnectCard"; // v2 PR-2C

/**
 * v1.2.A2 — Account profile (self-service).
 *
 * Three sections:
 *   1. Identity (read-only header + edit display name)
 *   2. Change email — current password required, non-blocking notification
 *   3. Change password — current password required, rotates session
 *
 * Per-user, NOT per-org. Lives inside the standard AppShell chrome — NOT
 * the SettingsLayout left-rail (settings is org-scoped, profile is user-scoped).
 *
 * Out of scope for this patch (PR-A2): sessions list, MFA, API tokens,
 * email-verification gate for new email.
 */
export default function AccountProfile() {
  const { user, refresh } = useAuth();

  const onTokensRotated = (accessToken, refreshToken) => {
    if (accessToken) setAuthToken(accessToken);
    if (refreshToken) localStorage.setItem("eb_refresh_token", refreshToken);
    refresh();
  };

  return (
    <AppShell>
      <div className="max-w-3xl" data-testid="account-profile">
        <div className="eb-overline mb-1">Account</div>
        <h1 className="text-3xl font-semibold text-navy-900 mb-2">My profile</h1>
        <p className="text-sm text-navy-500 mb-6">
          Your account, name, email, and password. Changes to email and password require your
          current password and are logged for security.
        </p>

        <IdentityCard user={user} />

        <div className="mt-6 grid gap-6 md:grid-cols-1">
          <EditNameCard user={user} onUpdated={refresh} />
          <ChangeEmailCard user={user} onUpdated={refresh} />
          <ChangePasswordCard onRotated={onTokensRotated} />
          {/* v1.2 PR-B.3 — three new cards */}
          <SessionsCard />
          <MfaCard />
          {/* v2 PR-2C — Connected calendars (Google for now, Microsoft via PR-2D). */}
          <CalendarConnectCard />
          <ThemeCard />
          {/* Phase B2 — per-user locale override card. `allowInherit` adds
              the "Use organisation default" sentinel so the user can clear
              their override and inherit the org's locale instead. */}
          <LocaleCard scope="user" initialLocale={user?.locale} allowInherit />
        </div>
      </div>
    </AppShell>
  );
}

// ---------- 1. Identity (read-only header) ----------

function IdentityCard({ user }) {
  const { formatDate, formatDateTime } = useLocale();
  const initials = (user?.name || user?.email || "?")
    .split(/[\s.@]+/).filter(Boolean).slice(0, 2)
    .map((s) => s[0]?.toUpperCase()).join("");
  const created = user?.created_at ? formatDate(user.created_at) : "—";
  const lastLogin = user?.last_login_at ? formatDateTime(user.last_login_at) : "—";

  return (
    <div className="bg-white border border-navy-100 rounded-2xl p-5 flex items-start gap-4" data-testid="profile-identity">
      <span className="w-16 h-16 rounded-full bg-navy-900 text-white text-xl font-semibold flex items-center justify-center flex-shrink-0">
        {initials || "?"}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-lg font-semibold text-navy-900" data-testid="profile-name">{user?.name || "—"}</div>
        <div className="text-sm text-navy-500 truncate" data-testid="profile-email">{user?.email}</div>
        <div className="mt-2 inline-flex items-center gap-2">
          <span className="text-[11px] uppercase tracking-wide bg-orange-500/10 text-orange-700 font-medium px-2 py-0.5 rounded">
            {user?.role?.replace("_", " ")}
          </span>
          <span className="text-xs text-navy-500" data-testid="profile-org-name">
            {user?.org_id ? `org: ${user.org_id.slice(0, 8)}…` : "No active org — super admin"}
          </span>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3 text-xs text-navy-500">
          <div>Created · <span className="text-navy-700 font-medium">{created}</span></div>
          <div>Last login · <span className="text-navy-700 font-medium">{lastLogin}</span></div>
        </div>
      </div>
    </div>
  );
}

// ---------- 2. Edit display name ----------

function EditNameCard({ user, onUpdated }) {
  const [name, setName] = useState(user?.name || "");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Name cannot be empty"); return; }
    setSaving(true);
    try {
      await api.patch("/auth/me", { name: name.trim() });
      toast.success("Name updated");
      onUpdated && onUpdated();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <form onSubmit={submit} className="bg-white border border-navy-100 rounded-2xl p-5" data-testid="profile-name-card">
      <div className="inline-flex items-center gap-2 mb-3">
        <UserCog className="w-4 h-4 text-orange-500" />
        <h2 className="font-semibold text-navy-900">Display name</h2>
      </div>
      <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end">
        <div>
          <Label htmlFor="name" className="eb-label">Name</Label>
          <Input id="name" value={name} onChange={(e) => setName(e.target.value)}
                 maxLength={200} data-testid="profile-name-input" />
        </div>
        <Button type="submit" disabled={saving || name === (user?.name || "")}
                className="bg-orange-500 hover:bg-orange-600 text-white"
                data-testid="profile-name-save">
          {saving ? "Saving…" : (<><Check className="w-4 h-4 mr-1" /> Save</>)}
        </Button>
      </div>
    </form>
  );
}

// ---------- 3. Change email ----------

function ChangeEmailCard({ user, onUpdated }) {
  const [newEmail, setNewEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) { toast.error("Enter a valid email"); return; }
    if (!currentPassword) { toast.error("Enter your current password to confirm"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/auth/me/change-email", {
        new_email: newEmail.trim(),
        current_password: currentPassword,
      });
      const notified = data.notification_delivered_old && data.notification_delivered_new;
      if (notified) {
        toast.success("Email updated. Security notifications sent to both addresses.");
      } else {
        toast.warning("Email change saved; security notification email could not be sent.");
      }
      setNewEmail(""); setCurrentPassword("");
      onUpdated && onUpdated();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 401 && detail?.error === "invalid_password") {
        toast.error("Current password is incorrect");
      } else if (status === 409 && detail?.error === "email_in_use") {
        toast.error("That email is already in use by another account");
      } else if (status === 429) {
        toast.error(`Rate limited — retry in ${detail?.retry_after_seconds || "?"}s`);
      } else if (status === 400 && detail?.error === "same_email") {
        toast.error("New email is the same as current");
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-navy-100 rounded-2xl p-5" data-testid="profile-email-card">
      <div className="inline-flex items-center gap-2 mb-3">
        <Mail className="w-4 h-4 text-orange-500" />
        <h2 className="font-semibold text-navy-900">Change email</h2>
      </div>
      <p className="text-xs text-navy-500 mb-4">
        Current email: <span className="font-mono text-navy-700">{user?.email}</span>. A security
        notification will be sent to both addresses.
      </p>
      <div className="grid gap-3">
        <div>
          <Label htmlFor="new-email" className="eb-label">New email</Label>
          <Input id="new-email" type="email" autoComplete="off" value={newEmail}
                 onChange={(e) => setNewEmail(e.target.value)}
                 data-testid="profile-new-email-input" />
        </div>
        <div>
          <Label htmlFor="current-pw-1" className="eb-label">Current password</Label>
          <Input id="current-pw-1" type="password" autoComplete="current-password"
                 value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)}
                 data-testid="profile-email-current-pw" />
        </div>
        <div className="flex justify-end">
          <Button type="submit" disabled={saving}
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                  data-testid="profile-email-save">
            {saving ? "Saving…" : "Save email"}
          </Button>
        </div>
      </div>
    </form>
  );
}

// ---------- 4. Change password ----------

function ChangePasswordCard({ onRotated }) {
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (newPw.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (newPw !== confirmPw) { toast.error("Passwords do not match"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/auth/me/change-password", {
        current_password: currentPw,
        new_password: newPw,
        confirm_new_password: confirmPw,
      });
      // Rotate session — write new tokens to localStorage
      if (data.access_token && onRotated) onRotated(data.access_token, data.refresh_token);
      toast.success(
        data.notification_delivered
          ? "Password updated. Security notification sent to your email."
          : "Password updated. (Notification email could not be sent — non-blocking.)",
      );
      setCurrentPw(""); setNewPw(""); setConfirmPw("");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 401 && detail?.error === "invalid_password") {
        toast.error("Current password is incorrect");
      } else if (status === 422 && detail?.error === "passwords_do_not_match") {
        toast.error("New passwords don't match");
      } else if (status === 400 && detail?.error === "same_as_current") {
        toast.error("New password cannot be the same as current");
      } else if (status === 429) {
        toast.error(`Rate limited — retry in ${detail?.retry_after_seconds || "?"}s`);
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-navy-100 rounded-2xl p-5" data-testid="profile-password-card">
      <div className="inline-flex items-center gap-2 mb-3">
        <Lock className="w-4 h-4 text-orange-500" />
        <h2 className="font-semibold text-navy-900">Change password</h2>
      </div>
      <p className="text-xs text-navy-500 mb-4 inline-flex items-center gap-1.5">
        <ShieldAlert className="w-3 h-3" /> Changing your password rotates your session and sends a
        security notification to your email.
      </p>
      <div className="grid gap-3">
        <div>
          <Label htmlFor="current-pw-2" className="eb-label">Current password</Label>
          <Input id="current-pw-2" type="password" autoComplete="current-password"
                 value={currentPw} onChange={(e) => setCurrentPw(e.target.value)}
                 data-testid="profile-pw-current" />
        </div>
        <div>
          <Label htmlFor="new-pw" className="eb-label">New password (min 8 chars)</Label>
          <Input id="new-pw" type="password" autoComplete="new-password" minLength={8}
                 value={newPw} onChange={(e) => setNewPw(e.target.value)}
                 data-testid="profile-pw-new" />
        </div>
        <div>
          <Label htmlFor="confirm-pw" className="eb-label">Confirm new password</Label>
          <Input id="confirm-pw" type="password" autoComplete="new-password" minLength={8}
                 value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)}
                 data-testid="profile-pw-confirm" />
        </div>
        <div className="flex justify-end">
          <Button type="submit" disabled={saving}
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                  data-testid="profile-pw-save">
            {saving ? "Saving…" : "Save password"}
          </Button>
        </div>
      </div>
    </form>
  );
}
